import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Row, Col, Table, Alert, Spinner } from 'react-bootstrap';
import { formatBalanceDisplay } from '../utils/formatBalance';

const PlanningModal = ({
  show,
  onHide,
  account,
  tradingStats,
  defaultStatsPeriod = 3,
  fetchTradingStats
}) => {
  const [formData, setFormData] = useState({
    accountSize: '',
    positionSize: '10%',
    desiredReturn: '100%'
  });

  const [calculations, setCalculations] = useState(null);
  const [currentStats, setCurrentStats] = useState(tradingStats);
  const [statsPeriod, setStatsPeriod] = useState(defaultStatsPeriod);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState('');

  // Preload form data when modal opens
  useEffect(() => {
    if (show) {
      const accountValue = Number(
        account?.balance ?? account?.current_value ?? account?.total_value ?? 0
      );
      const roundedAccountValue = accountValue > 0
        ? Math.ceil(accountValue / 1000) * 1000
        : accountValue;
      const normalizedAccountValue = Number.isFinite(roundedAccountValue)
        ? roundedAccountValue
        : '';

      setFormData({
        accountSize: normalizedAccountValue === '' ? '' : String(normalizedAccountValue),
        positionSize: '10%',
        desiredReturn: '100%'
      });
      setStatsPeriod(defaultStatsPeriod);
    }
  }, [show, account, defaultStatsPeriod]);

  useEffect(() => {
    if (show) {
      setCurrentStats(tradingStats);
      setStatsError('');
    }
  }, [show, tradingStats]);

  const calculateResults = useCallback(() => {
    const accountSize = parseFloat(formData.accountSize) || 0;
    const positionSizeMultiplier = parseFloat(formData.positionSize.replace('%', '')) || 1;
    const desiredReturnPercent = parseFloat(formData.desiredReturn.replace('%', '')) || 0;

    // Get stats from trading statistics
    const avgGainPct = currentStats?.average_gain_pct || 0;
    const avgLossPct = Math.abs(currentStats?.average_loss_pct || 0);
    const winRate = currentStats?.win_rate || 0;
    
    // Calculate outputs
    const goalAmount = accountSize * (desiredReturnPercent / 100);
    const positionSizeAmount = accountSize * (positionSizeMultiplier / 100);
    const gainLossRatio = avgLossPct > 0 ? avgGainPct / avgLossPct : 0;
    
    // Calculate expected net return per trade
    const expectedNetReturnPct = (winRate / 100) * avgGainPct - ((100 - winRate) / 100) * avgLossPct;
    const expectedNetReturnDollar = positionSizeAmount * (expectedNetReturnPct / 100);
    
    // Calculate number of trades needed
    const tradesNeeded = expectedNetReturnPct > 0 ? Math.ceil(goalAmount / expectedNetReturnDollar) : 0;
    
    // Calculate winning and losing trade counts
    const totalTrades = Math.ceil(tradesNeeded);
    const winningTrades = Math.ceil(totalTrades * (winRate / 100));
    const losingTrades = totalTrades - winningTrades;
    
    // Calculate average dollar amounts
    const avgGainDollar = positionSizeAmount * (avgGainPct / 100);
    const avgLossDollar = positionSizeAmount * (avgLossPct / 100);
    
    setCalculations({
      goalAmount,
      positionSizeAmount,
      gainLossRatio,
      tradesNeeded,
      avgGainDollar,
      winningTrades,
      avgLossDollar,
      losingTrades,
      expectedNetReturnPct,
      expectedNetReturnDollar
    });
  }, [formData, currentStats]);

  // Calculate results when form data changes
  useEffect(() => {
    if (formData.accountSize && formData.positionSize && formData.desiredReturn && currentStats) {
      calculateResults();
    }
  }, [formData, currentStats, calculateResults]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const positionSizeOptions = ['5%', '10%', '15%', '20%', '25%'];
  const desiredReturnOptions = ['50%', '100%', '150%', '200%', '250%', '300%'];
  const statsPeriodOptions = [
    { label: '1 Month', value: 1 },
    { label: '2 Months', value: 2 },
    { label: '3 Months', value: 3 },
    { label: '6 Months', value: 6 }
  ];

  const getPeriodDates = useCallback((months) => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);
    return { start, end: now };
  }, []);

  const getPeriodLabel = useCallback((months) => {
    const { start, end } = getPeriodDates(months);
    const formatterOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${start.toLocaleDateString('en-US', formatterOptions)} - ${end.toLocaleDateString('en-US', formatterOptions)}`;
  }, [getPeriodDates]);

  const handleStatsPeriodChange = useCallback(async (months) => {
    setStatsPeriod(months);

    if (!fetchTradingStats) {
      return;
    }

    setStatsLoading(true);
    setStatsError('');

    try {
      const updatedStats = await fetchTradingStats(months);
      setCurrentStats(updatedStats || null);
    } catch (err) {
      setStatsError('Unable to load trading statistics for the selected period.');
    } finally {
      setStatsLoading(false);
    }
  }, [fetchTradingStats]);

  useEffect(() => {
    if (show && fetchTradingStats) {
      handleStatsPeriodChange(defaultStatsPeriod);
    }
  }, [show, defaultStatsPeriod, fetchTradingStats, handleStatsPeriodChange]);

  useEffect(() => {
    if (!currentStats) {
      setCalculations(null);
    }
  }, [currentStats]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Trading Planning Calculator</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={6}>
            <h6 className="mb-3">Input Parameters</h6>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Account Size</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.accountSize}
                  onChange={(e) => handleInputChange('accountSize', e.target.value)}
                  placeholder="Enter account size"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Position Size (% of Account)</Form.Label>
                <Form.Select
                  value={formData.positionSize}
                  onChange={(e) => handleInputChange('positionSize', e.target.value)}
                >
                  {positionSizeOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Desired Return</Form.Label>
                <Form.Select
                  value={formData.desiredReturn}
                  onChange={(e) => handleInputChange('desiredReturn', e.target.value)}
                >
                  {desiredReturnOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
            
            <Alert variant="info" className="mt-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong className="me-2">Account Statistics</strong>
                <Form.Select
                  size="sm"
                  value={statsPeriod}
                  onChange={(e) => handleStatsPeriodChange(Number(e.target.value))}
                  disabled={statsLoading}
                  style={{ maxWidth: '160px' }}
                >
                  {statsPeriodOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="mb-2">
                <small className="text-muted">
                  Period: {getPeriodLabel(statsPeriod)}
                </small>
              </div>
              {statsLoading ? (
                <div className="d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading statistics...
                </div>
              ) : statsError ? (
                <div className="text-danger">{statsError}</div>
              ) : (
                <>
                  Average % Gain: {currentStats?.average_gain_pct || 0}%<br />
                  Average % Loss: {currentStats?.average_loss_pct || 0}%<br />
                  % of Winning Trades: {currentStats?.win_rate || 0}%
                </>
              )}
            </Alert>
          </Col>

          <Col md={6}>
            <h6 className="mb-3">Calculated Results</h6>
            {calculations ? (
              <Table striped bordered hover size="sm">
                <tbody>
                  <tr>
                    <td><strong>Goal $</strong></td>
                    <td className="text-end">{formatBalanceDisplay(calculations.goalAmount)}</td>
                  </tr>
                  <tr>
                    <td><strong>Position Size $</strong></td>
                    <td className="text-end">{formatBalanceDisplay(calculations.positionSizeAmount)}</td>
                  </tr>
                  <tr>
                    <td><strong>Gain/Loss Ratio</strong></td>
                    <td className="text-end">{calculations.gainLossRatio.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td><strong>Trades Needed</strong></td>
                    <td className="text-end">{calculations.tradesNeeded}</td>
                  </tr>
                  <tr>
                    <td><strong>Avg $ Gain (Winning)</strong></td>
                    <td className="text-end">{formatBalanceDisplay(calculations.avgGainDollar)}</td>
                  </tr>
                  <tr>
                    <td><strong>Winning Trades</strong></td>
                    <td className="text-end">{calculations.winningTrades}</td>
                  </tr>
                  <tr>
                    <td><strong>Avg $ Loss (Losing)</strong></td>
                    <td className="text-end">{formatBalanceDisplay(calculations.avgLossDollar)}</td>
                  </tr>
                  <tr>
                    <td><strong>Losing Trades</strong></td>
                    <td className="text-end">{calculations.losingTrades}</td>
                  </tr>
                  <tr>
                    <td><strong>Expected Net % per Trade</strong></td>
                    <td className="text-end">{calculations.expectedNetReturnPct.toFixed(2)}%</td>
                  </tr>
                  <tr>
                    <td><strong>Expected Net $ per Trade</strong></td>
                    <td className="text-end">{formatBalanceDisplay(calculations.expectedNetReturnDollar)}</td>
                  </tr>
                </tbody>
              </Table>
            ) : (
              <Alert variant="secondary">
                Enter account size to see calculations
              </Alert>
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PlanningModal;
